import {Component, OnInit, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {Router, RouterLink} from "@angular/router";
import {TranslatePipe} from "@ngx-translate/core";
import {CommonModule, NgOptimizedImage} from "@angular/common";
import {TemplatePortal} from "@angular/cdk/portal";
import {Overlay, OverlayPositionBuilder, OverlayRef} from '@angular/cdk/overlay';
import {LanguageService} from "../../../services/language.service";
import { LanguageChoice } from "src/app/config";
import {BreakpointObserver} from "@angular/cdk/layout";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {BugReportModalComponent} from "../bug-report-modal/bug-report-modal.component";
import {CookieService} from "ngx-cookie-service";
import {AuthService} from "../../../auth.service";
import {UserService} from "../../../services/users/users.service";
import {ChatbotComponent} from "../chatbot/chatbot.component";

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [
    RouterLink,
    TranslatePipe,
    NgOptimizedImage,
    CommonModule,
    ChatbotComponent
  ],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent implements OnInit {
  @ViewChild('languagePopupTemplate') languagePopupTemplate!: TemplateRef<any>;

  private overlayRef: OverlayRef | null = null;
  languages: readonly { code: LanguageChoice; label: string }[] = [];
  currentLang: LanguageChoice = 'es';
  showFloatingFooter = true;
  isInstitutionUser = false;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private positionBuilder: OverlayPositionBuilder,
    public languageService: LanguageService,
    private breakpoint: BreakpointObserver,
    private router: Router,
    private modalService: NgbModal,
    private cookieService: CookieService,
    private authService: AuthService,
    private userService: UserService
  ) {
    this.languages = this.languageService.getLanguages();
    this.currentLang = this.languageService.getChoice();
  }

  ngOnInit(): void {
    this.checkUserType();
  }

  private checkUserType(): void {
    const token = this.cookieService.get('gr_access');
    if (!token) return;

    const decodedToken = this.authService.getDecodedAccessToken(token);
    if (!decodedToken?.user_id) return;

    this.userService.getUserProfileData(decodedToken.user_id).subscribe({
      next: (user) => {
        const institutionTypes = ['Principal', 'Teacher', 'School'];
        this.isInstitutionUser = institutionTypes.includes(user.user_type);
      }
    });
  }

  isMobile = this.breakpoint.isMatched('(max-width: 980px)');

  switchLang(lang: LanguageChoice) {
    void this.languageService.switchLang(lang);
    this.currentLang = lang;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  openLanguagePopup(trigger: HTMLElement) {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      return;
    }

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(trigger)
      .withPositions([
        this.isMobile ?
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetX: 10, offsetY: -600 }  :
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetX: 176, offsetY: -280 },
      ])
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      panelClass: 'lang-overlay-pane',
      disposeOnNavigation: true,
    });

    this.overlayRef.backdropClick().subscribe(() => {
      this.overlayRef?.dispose();
      this.overlayRef = null;
    });

    const portal = new TemplatePortal(this.languagePopupTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  isActive(url: string): boolean {
    return this.router.url === url;
  }

  openBugReportModal(): void {
    if (this.modalService.hasOpenModals()) {
      return;
    }

    this.modalService.open(BugReportModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static'
    });
  }
}
